import { ToiStatusOption } from '../models/controle-toi.model';
import { PdfToiEventoItem } from '../models/pdf-toi.model';

export const STATUS_AGUARDANDO_VALIDACAO = 'AGUARDANDO_VALIDACAO';

/**
 * Nomes de status TOI conhecidos pelo front, usados só para normalizar rótulos e
 * regras de exibição (cores, somente leitura, contagem de dias).
 * O combo do filtro NÃO usa esta lista: ele vem de GET v3/MxlAtividadeToi/Status,
 * pois os IDs de MXL_ATIVIDADE_TOI_STATUS variam entre ambientes.
 */
export const TOI_STATUS_OPTIONS: ToiStatusOption[] = [
  { label: 'TOI Gerado', value: 'TOI Gerado', sigla: 'TOI_GERADO' },
  { label: 'Entregue em mãos', value: 'Entregue em mãos', sigla: 'TOI_ENTREGUE_MAOS' },
  {
    label: 'Aguardando validação',
    value: 'Aguardando validação',
    sigla: 'AGUARDANDO_VALIDACAO',
  },
  { label: 'Reprovado', value: 'Reprovado', sigla: 'REPROVADO' },
  { label: 'Aprovado', value: 'Aprovado', sigla: 'APROVADO' },
  {
    label: 'Aguardando envio Correios',
    value: 'Aguardando envio Correios',
    sigla: 'AGUARDANDO_ENVIO_CORREIOS',
  },
  {
    label: 'Pendente confirmação para envio',
    value: 'Pendente confirmação para envio',
    sigla: 'PENDENTE_CONFIRMACAO_ENVIO',
  },
  {
    label: 'Aguardando tratamento envio',
    value: 'Aguardando tratamento envio',
    sigla: 'AGUARDANDO_TRATAMENTO_ENVIO',
  },
  { label: 'TOI tratado', value: 'TOI tratado', sigla: 'TOI_TRATADO' },
  {
    label: 'Aguardando retorno AR',
    value: 'Aguardando retorno AR',
    sigla: 'AGUARDANDO_RETORNO_AR',
  },
  {
    label: 'Aguardando detalhes de entrega',
    value: 'Aguardando detalhes de entrega',
    sigla: 'AGUARDANDO_DETALHES_ENTREGA',
  },
  {
    label: 'Tentativa de entrega 1',
    value: 'Tentativa de entrega 1',
    sigla: 'TENTATIVA_ENTREGA_1',
  },
  {
    label: 'Tentativa de entrega 2',
    value: 'Tentativa de entrega 2',
    sigla: 'TENTATIVA_ENTREGA_2',
  },
  {
    label: 'Tentativa de entrega 3',
    value: 'Tentativa de entrega 3',
    sigla: 'TENTATIVA_ENTREGA_3',
  },
  { label: 'Entregue', value: 'Entregue', sigla: 'ENTREGUE' },
];

const TOI_STATUS_SIGLA_MAP = Object.fromEntries(
  TOI_STATUS_OPTIONS.map((o) => [o.sigla, o.value])
) as Record<string, string>;

/** Rótulos legados / variações da API mapeados para o valor canônico. */
const TOI_STATUS_API_ALIASES: Record<string, string> = {
  ...TOI_STATUS_SIGLA_MAP,
  Gerado: 'TOI Gerado',
  'TOI GERADO': 'TOI Gerado',
  Pendente: 'TOI Gerado',
  'Aguardando Validação': 'Aguardando validação',
  'Recusa de recebimento': 'Aguardando validação',
  'Aguardando Envio Correios': 'Aguardando envio Correios',
  'Envio pelos Correios': 'Aguardando envio Correios',
  'Pendente Confirmação para Envio': 'Pendente confirmação para envio',
  'Tentativa de Entrega 1': 'Tentativa de entrega 1',
  'Tentativa de Entrega 2': 'Tentativa de entrega 2',
  'Tentativa de Entrega 3': 'Tentativa de entrega 3',
};

function findToiStatusOptionByLabel(status: string): ToiStatusOption | undefined {
  return TOI_STATUS_OPTIONS.find(
    (option) => option.value.localeCompare(status, 'pt-BR', { sensitivity: 'accent' }) === 0
  );
}

export function normalizeToiStatusLabel(status?: string | null): string | undefined {
  const trimmed = status?.trim();
  if (!trimmed) {
    return undefined;
  }

  const direct = findToiStatusOptionByLabel(trimmed);
  if (direct) {
    return direct.value;
  }

  const alias =
    TOI_STATUS_API_ALIASES[trimmed] ??
    TOI_STATUS_API_ALIASES[trimmed.toUpperCase()] ??
    Object.entries(TOI_STATUS_API_ALIASES).find(
      ([key]) => key.localeCompare(trimmed, 'pt-BR', { sensitivity: 'accent' }) === 0
    )?.[1];

  if (alias) {
    return alias;
  }

  return trimmed;
}

export function isAguardandoValidacaoStatus(status?: string | null): boolean {
  return normalizeToiStatusLabel(status) === 'Aguardando validação';
}

export function resolveToiStatusSeverity(
  status: string
): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
  const normalized = normalizeToiStatusLabel(status) ?? status;

  switch (normalized) {
    case 'Entregue':
    case 'Entregue em mãos':
    case 'Aprovado':
    case 'TOI tratado':
      return 'success';
    case 'Reprovado':
      return 'danger';
    case 'Aguardando tratamento envio':
    case 'Pendente confirmação para envio':
    case 'Tentativa de entrega 1':
    case 'Tentativa de entrega 2':
    case 'Tentativa de entrega 3':
      return 'warn';
    case 'Aguardando validação':
    case 'Aguardando envio Correios':
    case 'Aguardando retorno AR':
    case 'Aguardando detalhes de entrega':
    case 'TOI Gerado':
      return 'info';
    default:
      return 'secondary';
  }
}

/** Status em que o TOI não permite mais edição (somente consulta). */
export const TOI_STATUS_SOMENTE_LEITURA = new Set<string>([
  'Entregue em mãos',
  'Reprovado',
  'Entregue',
]);

export function isToiStatusSomenteLeitura(status?: string | null): boolean {
  const normalized = normalizeToiStatusLabel(status);
  return normalized != null && TOI_STATUS_SOMENTE_LEITURA.has(normalized);
}

const TOI_STATUS_COM_CONTAGEM_DIAS = new Set<string>(['Entregue em mãos', 'Entregue']);

export function isToiStatusComContagemDias(status?: string | null): boolean {
  const normalized = normalizeToiStatusLabel(status);
  return normalized != null && TOI_STATUS_COM_CONTAGEM_DIAS.has(normalized);
}

export interface ToiStatusFlags {
  flagEntregueMaos: boolean;
  flagRecusaRecebimento: boolean;
  flagGerado: boolean;
  flagExigeEnvioCorreios: boolean;
  flagEnderecoPendenteTratamento?: boolean;
}

export function buildStatusToiLabel(flags: ToiStatusFlags): string {
  if (flags.flagEntregueMaos) return 'Entregue em mãos';
  if (flags.flagEnderecoPendenteTratamento) return 'Aguardando tratamento envio';
  if (flags.flagRecusaRecebimento) return 'Aguardando validação';
  if (flags.flagExigeEnvioCorreios) return 'Aguardando envio Correios';
  if (flags.flagGerado) return 'TOI Gerado';
  return 'TOI Gerado';
}

/** Status atual do TOI a partir do histórico de eventos (primeiro registro). */
export function resolveStatusToiFromEventos(
  eventosToi: PdfToiEventoItem[] | undefined | null,
  flags?: ToiStatusFlags
): string {
  const status = eventosToi?.[0]?.status?.trim();
  if (status) {
    return status;
  }
  if (flags) {
    return buildStatusToiLabel(flags);
  }
  return 'TOI Gerado';
}
